import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AuthApiError, emailAuth } from '@/api/auth/emailAuth';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/locale';

import { AuthLayout } from './AuthLayout';
import { AuthField, FormAlert, SubmitButton } from './fields';
import { focusField, friendlyAuthError, LABEL_ACTION_HIT } from './form';
import { sanitizeNext } from './next';

/*
 * The code confirms the account with Cognito; signing in still needs the
 * password, so a successful confirm hands back to /login with a success toast
 * (task contract) and the email prefilled.
 */
export function ConfirmScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = useT();
  const hasEmailParam = Boolean(searchParams.get('email'));
  const next = sanitizeNext(searchParams.get('next'));

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [code, setCode] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // The 30s resend cooldown only starts after a successful resend — the first
  // one is always available (the email may simply not have arrived).
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = window.setInterval(() => {
      setResendCooldown((count) => (count <= 1 ? 0 : count - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldown]);

  const loginTo = `/login${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ''}${
    next === '/' ? '' : `${email.trim() ? '&' : '?'}next=${encodeURIComponent(next)}`
  }`;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const nextEmailError = /^\S+@\S+\.\S+$/.test(email.trim()) ? null : t('confirm.enterEmail');
    const nextCodeError = /^\d{6}$/.test(code.trim()) ? null : t('confirm.enterCode');
    setEmailError(nextEmailError);
    setCodeError(nextCodeError);
    setFormError(null);
    if (nextEmailError || nextCodeError) {
      focusField(nextEmailError ? 'email' : 'code');
      return;
    }

    setBusy(true);
    try {
      await emailAuth.confirm(email.trim(), code.trim());
      toast.success(t('confirm.success'));
      navigate(loginTo);
    } catch (error) {
      if (error instanceof AuthApiError && error.status === 0) {
        setFormError(t('login.error.network'));
        return;
      }
      setCodeError(friendlyAuthError(error, t, 'confirm.codeFail'));
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setEmailError(t('confirm.enterEmail'));
      focusField('email');
      return;
    }
    setEmailError(null);
    setResending(true);
    try {
      await emailAuth.resendCode(email.trim());
      toast.success(t('confirm.resendSuccess'));
      setResendCooldown(30);
    } catch (error) {
      setFormError(friendlyAuthError(error, t, 'confirm.resendFail'));
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthLayout
      title={t('confirm.title')}
      lede={
        hasEmailParam && email.trim() ? (
          <>
            {t('confirm.codeSentBefore')}{' '}
            <span className="font-semibold text-ink">{email.trim()}</span>
          </>
        ) : (
          t('confirm.noEmail')
        )
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
        {formError ? <FormAlert>{formError}</FormAlert> : null}
        {!hasEmailParam ? (
          <AuthField
            id="email"
            label={t('login.email')}
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            value={email}
            error={emailError}
            disabled={busy}
            onChange={(event) => {
              setEmail(event.target.value);
              setEmailError(null);
            }}
          />
        ) : null}
        <AuthField
          id="code"
          label={t('confirm.code')}
          className="h-14 text-center text-timecode text-[1.5rem] tracking-[0.4em]"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          error={codeError}
          disabled={busy}
          onChange={(event) => {
            setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
            setCodeError(null);
          }}
          labelTrailing={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={`${LABEL_ACTION_HIT} -mr-2 h-auto px-2 py-0.5 text-caption`}
              disabled={resending || busy || resendCooldown > 0}
              onClick={() => void onResend()}
            >
              {resendCooldown > 0
                ? t('confirm.resendIn', { count: resendCooldown })
                : t('confirm.resend')}
            </Button>
          }
        />
        <SubmitButton busy={busy}>{t('confirm.submit')}</SubmitButton>
      </form>

      <p className="mt-8 text-subhead text-ink-secondary">
        <Link
          to="/signup"
          className="font-semibold text-ink underline decoration-border decoration-2 underline-offset-4 transition-colors duration-150 hover:decoration-accent"
        >
          {t('confirm.differentEmail')}
        </Link>
      </p>
    </AuthLayout>
  );
}
